import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { InteractionStatus, AuthenticationResult, AccountInfo, EventMessage, EventType, InteractionType, IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';

import { ApplicationConfigService } from 'src/service/application-config.service';
import { UserService } from 'src/api/services';
import { UserProfileModel } from 'src/api/models';
import { NavigationItem, NavigationSection } from 'src/app/models/navigation';
import { from, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Injectable()
export class AzureAuthService implements OnDestroy {
  private readonly GRAPH_ENDPOINT = 'https://graph.microsoft.com/v1.0';
  
  public isLoggedIn = false;
  public isIframe = false;
  private unsubscribe$: Subject<void> = new Subject<void>();
  public userProfile: UserProfileModel;
  public account: AccountInfo;
  public accountRoles: string[]; // an array of strings
  private owner: boolean = false;

  constructor(
    private appConfig: ApplicationConfigService,
    private msalService: MsalService,
    private broadcastService: MsalBroadcastService,
    private userService: UserService,
    private router: Router,
    private applicationConfigService: ApplicationConfigService
  ) { }

  public isInRole(role: string): boolean {
    if (this.accountRoles == null){
      return false;
    }
    return this.accountRoles.find(s => s == role) != null;
  }

  public isAuthenticated(): boolean {
    const auth = this.getAccount() != null;
    if (!auth){
      var all = this.msalService.instance.getAllAccounts();
      if (all.length >= 1){
        // do we select that one user as active?
        //this.msalService.loginPopup();
      }
    }
    return auth;
  }

  public isDashboardEditor(): boolean {
    return this.accountRoles != null && (
      this.accountRoles.find(s => s == "admin") != null || 
      this.accountRoles.find(s => s == "dashboard") != null || 
      this.accountRoles.find(s => s == "dashboardEditor") != null);
  }

  public isGlobalViewer(): boolean {
    return this.accountRoles != null && (this.accountRoles.find(s => s == "admin") != null || this.accountRoles.find(s => s == "globalViewer") != null);
  }

  public isAdmin(): boolean {
    return this.accountRoles != null && this.accountRoles.find(s => s == "admin") != null;
  }

  public isOwner(): boolean {
    return this.owner || this.isAdmin();
  }

  canViewPage(page: NavigationItem): boolean {
    if (page.name == "Analysis" && this.userProfile.disableAnalysis){
      return false;
    }
    if (page.hidden) return false;
    if (page.isAdmin) return this.isAdmin();
    if (page.isOwner && !this.isOwner()) return false;
    if (page.role) return this.isInRole(page.role) || this.isAdmin();
    return true;
  }

  canViewSection(section: NavigationSection) {
    for (let page of section.pages){
      if (this.canViewPage(page)) return true;
    }
    return false;
  }

  /*private firstPageRedirect() {
    const url = localStorage.getItem("startingUrl");
    const cur = window.location.href;
    localStorage.removeItem("startingUrl");
    // redirect to the specified location unless its the same as the current url
    if (url && url != cur){
      // redirect to this URL
      console.log("REDIRECTING TO " + url);      
      // disabled redirect to saved page - might be causing strange behaviour on phone
      //window.location.href = url;
    }
  }

  private setFirstPageRedirect() {
    const url = localStorage.getItem("startingUrl");
    if (url == null){
      localStorage.setItem("startingUrl", window.location.href);
      console.log("Starting URL set to " + window.location.href);
    }
  }*/

  public onInit() {
    console.log("AUTH INIT");
    this.isIframe = window !== window.parent && !window.opener;

    this.broadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      takeUntil(this.unsubscribe$)
    ).subscribe(() => {
      this.checkAccount();
    });

    this.broadcastService.msalSubject$.pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      takeUntil(this.unsubscribe$)
    ).subscribe((result: EventMessage) => {
      const authResult = result.payload as AuthenticationResult;
      this.msalService.instance.setActiveAccount(authResult.account);
      console.log("Authentication Result: ", authResult.account.username);
      this.getAccount();
    });

    this.broadcastService.msalSubject$.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe(e => {
      console.log('Auth Event', e);
    });

    this.msalService.handleRedirectObservable().subscribe({
      error: (err) => console.error('MSAL handleRedirectObservable error:', err)
    });


    if (this.isLoggedIn) {
      this.getAccount();
    }    
  }

  checkAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount();
    this.isLoggedIn = !!activeAccount;
    this.appConfig.loggedIn = this.isLoggedIn;
  }

  public getAccount(): AccountInfo {
    if (this.account == null){
      this.account = this.msalService.instance.getActiveAccount();
      this.accounts = this.msalService.instance.getAllAccounts();
    }
    if (this.account == null){
      
      if (this.accounts.length > 0){
        this.msalService.instance.setActiveAccount(this.accounts[0]);
        this.account = this.msalService.instance.getActiveAccount();
      }
    }
    if (this.account && Array.isArray(this.account.idTokenClaims['roles'])){
      this.accountRoles = (this.account.idTokenClaims['roles'] as unknown) as string[];  
    } else {
      //this.accountRoles = [this.account.idTokenClaims['roles']  as string[]];
    }
    
    return this.account;
  }

  accounts: AccountInfo[];
  public getAccounts(): AccountInfo[]{
    return this.msalService.instance.getAllAccounts();
  }

  public changeAccount(account: AccountInfo){
    this.msalService.instance.setActiveAccount(account);
    window.location.reload();
  }

  public loginNewAccount(){
    const loginRequest = {
      scopes: ["user.read"],  // Adjust the scopes as needed
      prompt: "select_account"  // Force the account selection prompt
    };
    this.msalService.instance.setActiveAccount(null);
    this.msalService.instance.loginPopup(loginRequest).then(acc => {
      console.log(acc);
      this.msalService.instance.setActiveAccount(acc.account);
      this.getAccount();
    });
  }

  idToken: string;

   // Get token method
   getToken(): Promise<string | null> {
    console.log(`AzureAuthService.GetToken() -> Starting with acquireTokenSilent`)
    return this.msalService.instance.acquireTokenSilent({ scopes: ['User.Read'] })
        .then((result: AuthenticationResult) => {
          this.idToken = result.idToken;
          return result.idToken;
        })
        .catch(() => {
            console.log(`AzureAuthService.GetToken() -> Fallback to loginPopup`)
            // Fallback to interactive login if silent token acquisition fails
            return this.msalService.instance.loginPopup({ scopes: ['User.Read'] })
                .then((result: AuthenticationResult) => {
                    this.idToken = result.idToken;
                    return result.idToken;
                });
        });
  }

  
  public login(): Observable<AuthenticationResult> {
    console.log('AzureAuthService.Login()')
    if (!this.anon) {
      const loginRequest = {
        scopes: ['user.read']
      };
      return from(this.msalService.loginPopup(loginRequest));
    } else {
      return from(Promise.reject('Anonymous login is not permitted'));
    }
  }
  

  public logout() {
    this.msalService.logoutPopup();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  anon: boolean = false;
  forceAnonymous(){
    this.anon = true;
  }

  userProfileCreateReturn(): Promise<UserProfileModel> {
    if(!this.account)
    {
      this.getAccount();
    }

    if (this.userProfile) {
      return Promise.resolve(this.userProfile);
    }

    return new Promise((resolve, reject) => {
      this.userService.UserRegisterUser().subscribe(
        res => {
          this.userProfile = res;
          console.log("UserProile", res);

          this.applicationConfigService.setDashboardMenu();
          this.applicationConfigService.setSiteTypeMenu(res.customerId);
          this.applicationConfigService.setCustomersMenu(res.customerId);

          if (res.applicationName) this.applicationConfigService.applicationName = res.applicationName;
          if (res.logoUrl) this.applicationConfigService.logoUrl = res.logoUrl;
          if (res.homeScreen) this.applicationConfigService.homeScreen = res.homeScreen;
          if (res.backgroundImageUrl) this.applicationConfigService.backgroundImageUrl = res.backgroundImageUrl;
          if (res.themeColour) this.applicationConfigService.themeColour = res.themeColour;

          this.applicationConfigService.setStyles();
          
          //(this.account as any).isOwner = res.isOwner;
          this.owner = res.isOwner;

          resolve(res);
        },
        err => {
          console.error('Failed to register user, redirecting to unauthorised:', err);
          this.router.navigate(['/unauthorised']);
          reject(err);
        }
      );
    });
  }
}
